import React, { useEffect, useMemo, useState } from 'react'
import {
  FormField,
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
  FormStringTagsField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SelectClientModal } from '@/routes/Transactions/SelectClient.modal'
import { fetchTickmillProductsAction } from '@/store/clients/clients.actions'
import { useApiClient } from '@/utils/api/ApiClient'
import { ClientsApiClient } from '@/utils/api/clientsApi'
import { DataLayoutScopeProvider } from '@/utils/DataLayoutContext'
import { isEmpty } from '@/utils/is-empty'
import { ClientDto } from 'api/Clients/Types/Client'
import { Language } from 'api/Clients/Types/Language'
import { LeadDto } from 'api/Clients/Types/LeadDto'
import { TickmillCompanyTypes } from 'api/Clients/Types/TickmillCompanyTypes'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { CountryDto } from 'api/Identity/Types/Country'
import { SignInResponse } from 'api/Identity/Types/SignInResponse'
import { LeadGenderType } from 'api/Leads/Types/LeadType'
import {
  Email,
  EmailPreviewModel,
  EmailPreviewRequest,
  ModelProperties,
} from 'api/Trading-Accounts/Types/EmailsDetailsDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import SelectLeadTable from 'routes/Records/Leads/SelectLeadTable'
import { useAppDispatch, useAppSelector } from 'store'
import { EmailPattern, UrlPattern } from 'utils/formValidation'
import { getEnumEntries } from 'utils/getEnumEntries'
import { camelCaseToSeparatedWords } from 'utils/string.utils'

import { usePublicDomainsFetch } from '../Countries/usePublicDomainsFetch'

export interface EmailPreviewValues {
  client?: ClientDto
  lead?: LeadDto
  method: EmailPreviewMethod
  template: string
  email?: string
  url?: string
  employee: {
    name: string
    position: string
  }
  genderId?: number
  tickmillProducts?: NameDto[]
  tickmillCompanyId?: number
  tickmillPublicDomain: string
  firstName?: string
  language: string
  lastName?: string
  countryId?: string
  model?: EmailPreviewModel
}

enum EmailPreviewMethod {
  ClientId = 'clientId',
  LeadId = 'leadId',
  Manual = 'manual',
}

const EmailDataModalUI = (props: FormikProps<EmailPreviewValues> & OuterProps) => {
  const { values, setFieldValue, errors, data, isSubmitting, isValid } = props
  const { countries, languages, tickmillCompanies } = props
  const dispatch = useAppDispatch()
  const clientsApiClient = useApiClient(ClientsApiClient)
  const tickmillProducts = useAppSelector((state) => state.clients.tickmillProducts)
  const [clientModal, setClientModal] = useState<boolean>(false)
  const [leadModal, setLeadModal] = useState<boolean>(false)

  const onCancel = () => {
    props.resetForm()
    props.onCancel()
  }

  useEffect(() => {
    if (isEmpty(tickmillProducts)) {
      dispatch(fetchTickmillProductsAction(clientsApiClient))
    }
  }, [])

  const { addPublicDomains: publicDomains = [], isLoading: publicDomainsLoading } =
    usePublicDomainsFetch(values.tickmillCompanyId)

  const fields = useMemo(() => {
    if (values.method == EmailPreviewMethod.ClientId) {
      return (
        <FormReadonlyField
          label='Client'
          value={values.client?.name ?? ''}
          error={errors.client as string}
          rightAddon={
            <Button type='button' variant='outline' size='sm' onClick={() => setClientModal(true)}>
              Select
            </Button>
          }
        />
      )
    } else if (values.method == EmailPreviewMethod.LeadId) {
      return (
        <FormReadonlyField
          label='Lead'
          value={values.lead ? `${values.lead.firstName} ${values.lead.lastName}` : ''}
          error={errors.lead as string}
          rightAddon={
            <Button type='button' variant='outline' size='sm' onClick={() => setLeadModal(true)}>
              Select
            </Button>
          }
        />
      )
    }

    return (
      <>
        <FormSelectField
          name='genderId'
          label='Gender'
          dataTest='gender-field'
          options={getEnumEntries(LeadGenderType).map(([key, value]) => ({
            id: value,
            name: key,
          }))}
          error={errors.genderId as string}
          onChange={(value) => setFieldValue('genderId', value ? Number(value) : undefined)}
        />
        <FormField name='email' label='Email' dataTest='email-field' error={errors.email} />
        <FormField
          name='firstName'
          label='First Name'
          dataTest='first-name-field'
          error={errors.firstName}
        />
        <FormField
          name='lastName'
          label='Last Name'
          dataTest='last-name-field'
          error={errors.lastName}
        />
        <FormSelectField
          name='countryId'
          label='Country'
          dataTest='country-field'
          options={countries}
          error={errors.countryId as string}
          onChange={(value) => setFieldValue('countryId', value ? String(value) : '')}
        />
        <FormMultiSelectField
          name='tickmillProducts'
          label='Product(s)'
          options={tickmillProducts}
          value={(values.tickmillProducts || []).map((product) => product.id)}
          error={errors.tickmillProducts as string}
          onChange={(nextValue) => {
            const selectedValues = Array.isArray(nextValue)
              ? nextValue
              : nextValue == null
                ? []
                : [nextValue]

            props.setFieldValue(
              'tickmillProducts',
              selectedValues
                .map((selectedValue) =>
                  tickmillProducts.find((product) => product.id === selectedValue)
                )
                .filter(Boolean) as NameDto[]
            )
          }}
        />
      </>
    )
  }, [values, errors, countries, tickmillProducts])

  const onSelectClient = (client: ClientDto) => {
    setClientModal(false)
    setFieldValue('tickmillCompanyId', client.tickmillCompany.id)
    setFieldValue('client', client)
  }

  const onSelectLead = (lead: LeadDto) => {
    setLeadModal(false)
    setFieldValue('tickmillCompanyId', lead.tickmillCompany.id)
    setFieldValue('lead', lead)
  }

  const renderField = (key: string, value: ModelProperties) => {
    const fieldName = `model.${key}`

    if (key === 'products') {
      return (
        <FormMultiSelectField
          name={fieldName}
          label='Products'
          options={tickmillProducts}
          value={(values?.model?.products ?? []).map((p) => p.id)}
          onChange={(nextValue) => {
            const selectedValues = Array.isArray(nextValue)
              ? nextValue
              : nextValue == null
                ? []
                : [nextValue]

            setFieldValue(
              fieldName,
              selectedValues
                .map((selectedValue) =>
                  tickmillProducts.find((product) => product.id === selectedValue)
                )
                .filter(Boolean)
            )
          }}
        />
      )
    }

    switch (value.type) {
      case 'IEnumerable<String>':
        return (
          <FormStringTagsField
            name={fieldName}
            label={camelCaseToSeparatedWords(key)}
            value={(values?.model?.[key] as string[] | null) ?? []}
            onChange={(nextValues) => {
              if (isEmpty(nextValues)) {
                setFieldValue(fieldName, null)
              } else {
                setFieldValue(fieldName, nextValues)
              }
            }}
          />
        )
      case 'Boolean':
        return (
          <FormSelectField
            name={fieldName}
            label={camelCaseToSeparatedWords(key)}
            value={String(values?.model?.[key] ?? value.defaultValue ?? false)}
            options={[
              { id: 'true', name: 'Yes' },
              { id: 'false', name: 'No' },
            ]}
            onChange={(nextValue) => {
              setFieldValue(fieldName, nextValue ?? 'false')
            }}
          />
        )
      default:
        return <FormField name={fieldName} label={camelCaseToSeparatedWords(key)} />
    }
  }

  return (
    <React.Fragment>
      <SelectClientModal
        open={clientModal}
        onOpenChange={setClientModal}
        getClient={onSelectClient}
      />
      <Dialog open={leadModal} onOpenChange={(open) => !open && setLeadModal(false)}>
        <SelectLeadTable
          singleLead
          onClose={() => setLeadModal(false)}
          onSelectSingle={onSelectLead}
        />
      </Dialog>
      <Dialog open onOpenChange={(open) => !open && onCancel()}>
        <DialogContent className='flex max-h-[90vh] max-w-[760px] flex-col overflow-hidden'>
          <DialogHeader className='shrink-0 pr-10'>
            <DialogTitle>{`Generate Email - ${data?.name ?? ''}`}</DialogTitle>
          </DialogHeader>

          <DataLayoutScopeProvider>
            <Form className='flex min-h-0 flex-col gap-4'>
              <div className='min-h-0 overflow-y-auto pr-1'>
                <div className='grid gap-6 md:grid-cols-2'>
                  <section className='grid content-start gap-3'>
                    <FormSelectField
                      name='method'
                      label='Method'
                      value={values.method}
                      options={[
                        { id: EmailPreviewMethod.ClientId, name: 'Client ID' },
                        { id: EmailPreviewMethod.LeadId, name: 'Lead ID' },
                        { id: EmailPreviewMethod.Manual, name: 'Manual' },
                      ]}
                      onChange={(value) => setFieldValue('method', value)}
                    />
                    {fields}
                    <FormField name='url' label='URL' dataTest='url-field' error={errors.url} />
                    <FormSelectField
                      name='language'
                      label='Language'
                      options={languages}
                      showSearch
                      allowClear
                      error={errors.language as string}
                      onChange={(value) => setFieldValue('language', value)}
                    />
                    <FormSelectField
                      name='tickmillCompanyId'
                      label='Tickmill Company'
                      options={tickmillCompanies}
                      showSearch
                      disabled={values.method !== EmailPreviewMethod.Manual}
                      allowClear
                      error={errors.tickmillCompanyId as string}
                      onChange={(value) => {
                        setFieldValue('tickmillCompanyId', value ? Number(value) : undefined)
                        setFieldValue('tickmillPublicDomain', undefined)
                      }}
                    />
                    <FormSelectField
                      name='tickmillPublicDomain'
                      label='Public Domain'
                      options={publicDomains.map((domain) => ({
                        id: domain.name,
                        name: domain.name,
                      }))}
                      showSearch
                      disabled={publicDomainsLoading}
                      allowClear
                      error={errors.tickmillPublicDomain as string}
                      onChange={(value) => setFieldValue('tickmillPublicDomain', value)}
                    />
                  </section>

                  {!!data && !!Object.keys(data?.modelProperties).length && (
                    <section className='grid content-start gap-3'>
                      <h3 className='text-sm font-semibold text-foreground'>Model Properties</h3>
                      {Object.entries(data.modelProperties).map(([key, value]) => (
                        <React.Fragment key={key}>{renderField(key, value)}</React.Fragment>
                      ))}
                    </section>
                  )}
                </div>
              </div>

              <DialogFooter className='shrink-0'>
                <Button disabled={isSubmitting} onClick={onCancel} type='button' variant='outline'>
                  Cancel
                </Button>
                <Button key='submit-button' disabled={!isValid || isSubmitting} type='submit'>
                  Submit
                </Button>
              </DialogFooter>
            </Form>
          </DataLayoutScopeProvider>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  )
}

interface OuterProps {
  data?: Email
  me?: SignInResponse
  countries: CountryDto[]
  tickmillCompanies: TickmillCompanyDto[]
  languages: Language[]
  values?: EmailPreviewValues
  onSubmit(values: EmailPreviewRequest, rawValues: EmailPreviewValues): Promise<void>
  onCancel(): void
}

const dataToSubmit = (values: EmailPreviewValues, d?: Email): EmailPreviewRequest => {
  const { language, tickmillPublicDomain, employee, tickmillCompanyId, url, template } = values
  const tickmillProductIds = (values.tickmillProducts ?? []).map((product) => product.id)

  const commonValues = {
    language,
    tickmillPublicDomain,
    tickmillCompanyId,
    model: (() => {
      const model = isEmpty(values.model)
        ? Object.entries(d?.modelProperties ?? {}).reduce<Record<string, unknown>>(
            (acc, [key, value]) => {
              acc[key] = value.defaultValue
              return acc
            },
            {}
          )
        : values.model

      if (model) {
        const platformValue = model.platform

        if (typeof platformValue === 'string') {
          model.platform = { platform: platformValue }
        } else if (!platformValue) {
          model.platform = {}
        }
      }

      return model
    })(),
    url,
    template,
    employee,
  }

  if (values.method === EmailPreviewMethod.ClientId) {
    return { ...commonValues, clientId: values.client?.id }
  } else if (values.method === EmailPreviewMethod.LeadId) {
    return { ...commonValues, leadId: values.lead?.id }
  } else {
    return {
      ...commonValues,
      to: values.email,
      person: {
        genderId: Number(values.genderId ?? LeadGenderType.Unknown),
        firstName: values.firstName ?? '',
        lastName: values.lastName ?? '',
        productIds: tickmillProductIds,
      },
    }
  }
}

const getModelValues = (modelProperties?: {
  [key: string]: ModelProperties
}): Record<string, string> =>
  Object.entries(modelProperties ?? {})
    .filter(([, value]) => value.type === 'Boolean')
    .reduce<Record<string, string>>((acc, [key, value]) => {
      acc[key] = `${value.defaultValue}`
      return acc
    }, {})

export const EmailDataModal = withFormik<OuterProps, EmailPreviewValues>({
  mapPropsToValues: (props) => {
    if (props.values) return props.values
    return {
      client: undefined,
      tickmillPublicDomain: '',
      lead: undefined,
      tickmillCompanyId: TickmillCompanyTypes.Tickmill_SC_Company,
      method: EmailPreviewMethod.ClientId,
      template: props.data?.name ?? '',
      email: '',
      genderId: undefined,
      employee: {
        name: (props.me?.firstName ?? '') + ' ' + (props.me?.lastName ?? ''),
        position: props.me?.roles.join(', ') ?? '',
      },
      tickmillProducts: [],
      firstName: '',
      lastName: '',
      countryId: '',
      language: 'en',
      model: getModelValues(props.data?.modelProperties),
    }
  },
  handleSubmit: async (values, { props }) => {
    await props.onSubmit(dataToSubmit(values, props.data), values)
  },
  validate: (values) => {
    const errors: FormikErrors<EmailPreviewValues> = {}

    if (values.method === EmailPreviewMethod.Manual) {
      if (!values.email) errors.email = 'Required'
      if (!!values.email && !EmailPattern.test(values.email)) errors.email = 'Invalid Email'
      if (!values.firstName) errors.firstName = 'Required'
      if (!values.lastName) errors.lastName = 'Required'
      if (!values.genderId) errors.genderId = 'Required'
      if (!values.countryId) errors.countryId = 'Required'
      if (!values.tickmillProducts) errors.tickmillProducts = 'Required'
    } else if (values.method === EmailPreviewMethod.ClientId) {
      if (!values.client) errors.client = 'Required'
    } else if (values.method === EmailPreviewMethod.LeadId) {
      if (!values.lead) errors.lead = 'Required'
    }

    if (!values.language) errors.language = 'Required'
    if (!values.tickmillPublicDomain) errors.tickmillPublicDomain = 'Required'
    if (!values.tickmillCompanyId) errors.tickmillCompanyId = 'Required'

    if (!!values.url && !UrlPattern.test(values.url)) {
      errors.url = 'Invalid URL. Please start with https://.'
    }
    return errors
  },
  isInitialValid: (props) => {
    if (props.values) return true
    return false
  },
})(EmailDataModalUI)
